import React, { useEffect, useRef, useState } from 'react';

export default function ScrollReveal({
  children,
  className = '',
  animation = 'fade-in-up', // 'fade-in-up', 'fade-in-down', 'fade-in-left', 'fade-in-right', 'scale-up', 'fade-in'
  delay = 0, // ms
  duration = 800, // ms
  threshold = 0.05,
  once = true
}) {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsIntersecting(true);
          if (once && ref.current) {
            observer.unobserve(ref.current);
          }
        } else if (!once) {
          setIsIntersecting(false);
        }
      },
      { threshold }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [threshold, once]);

  const getAnimationStyles = () => {
    const baseStyles = {
      transitionProperty: 'opacity, transform',
      transitionDuration: `${duration}ms`,
      transitionDelay: `${delay}ms`,
      transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
      willChange: 'opacity, transform',
    };

    if (isIntersecting) {
      return {
        ...baseStyles,
        opacity: 1,
        transform: 'translate(0, 0) scale(1)',
      };
    }

    // Initial states
    switch (animation) {
      case 'fade-in-up':
        return {
          ...baseStyles,
          opacity: 0,
          transform: 'translateY(40px)',
        };
      case 'fade-in-down':
        return {
          ...baseStyles,
          opacity: 0,
          transform: 'translateY(-40px)',
        };
      case 'fade-in-left':
        return {
          ...baseStyles,
          opacity: 0,
          transform: 'translateX(-40px)',
        };
      case 'fade-in-right':
        return {
          ...baseStyles,
          opacity: 0,
          transform: 'translateX(40px)',
        };
      case 'scale-up':
        return {
          ...baseStyles,
          opacity: 0,
          transform: 'scale(0.93)',
        };
      case 'fade-in':
      default:
        return {
          ...baseStyles,
          opacity: 0,
        };
    }
  };

  return (
    <div
      ref={ref}
      style={getAnimationStyles()}
      className={className}
    >
      {children}
    </div>
  );
}
